/**
 * FormViewerWrapper
 *
 * Function-component wrapper that initializes and coordinates the hooks used
 * by the returns form viewer flow, then passes their state and actions to
 * FormViewer via props.
 *
 * Several refs are also shared with FormViewer so it can register callback
 * implementations that the hooks invoke later. This keeps the hook wiring in
 * one place and avoids tight coupling or circular imports between the hooks
 * and FormViewer.
 */
import React, { useCallback, useMemo, useRef } from 'react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import WebHelpers from '~/app/webHelpers.js';
import { isSuperOrTechUserReadOnly } from '~/app/Utility/general.js';
import FormViewer, { mapStateToProps, mapDispatchToProps } from './FormViewer.jsx';
import useCalcSession from './hooks/useCalcSession';
import useFormNavigation from './hooks/useFormNavigation';
import useFormFieldEvents from './hooks/useFormFieldEvents';
import useVerify from './hooks/useVerify';
import usePrint from './hooks/usePrint';
import useBookmarks from './hooks/useBookmarks';
import useFormPopulation from './hooks/useFormPopulation';
import useSpinner from './hooks/useSpinner';
import useSnackbars from './hooks/useSnackbars';
import useReturnClose from './hooks/useReturnClose';
import useSocketMessage from './hooks/useSocketMessage';
import useButtonMenu from './hooks/useButtonMenu';
import useCalcError from './hooks/useCalcError';
import useK1Manager from './hooks/useK1Manager';
import usePaymentCapture from './hooks/usePaymentCapture';
import useDocumentAttach from './hooks/useDocumentAttach';
import useQueueReturn from './hooks/useQueueReturn';
import useDepreciation from './hooks/useDepreciation';
import useTVA from './hooks/useTVA';
import useCopyReturn from './hooks/useCopyReturn';
import useTextLink from './hooks/useTextLink';
import useSwitchReturn from './hooks/useSwitchReturn';
import useInterviewMode from './hooks/useInterviewMode';
import useImportStock from './hooks/useImportStock';
import useAddForm from './hooks/useAddForm';
import useMiscState from './hooks/useMiscState';
import useReturnLock from './hooks/useReturnLock';
import useSpecialFormLoad from './hooks/useSpecialFormLoad';
import useTransferData from './hooks/useTransferData';
import useEncryptedPDF from './hooks/useEncryptedPDF';
import useChoiceList from './hooks/useChoiceList';
import useMenuCommand from './hooks/useMenuCommand';
import useFormErrors from './hooks/useFormErrors';
import useWallet from './hooks/useWallet';
import useSubmitForReview from './hooks/useSubmitForReview';
import useRestrictedFields from './hooks/useRestrictedFields';
import useCalcProcess from './hooks/useCalcProcess';
import usePDFAttach from './hooks/usePDFAttach';
import useConsentModal from './hooks/useConsentModal';

const FormViewerWrapper = props => {
  // JWT payload — stable for the lifetime of this session
  const payload = useMemo(() => WebHelpers.getJWTPayload(), []);

  // ─── Refs shared with the class component via props ───────────────────────

  // Session hook
  const returnInitializedRef = useRef(false);
  const onMessageRef = useRef(null); // FormViewer sets this to handleSocketMessage
  const onOpenRef = useRef(null); // FormViewer sets this to handleSessionOpen
  const onCloseRef = useRef(null); // FormViewer sets this to onCloseActiveReturn

  // Navigation hook
  const currentElRef = useRef(null); // currently focused field element
  const bookmarkFieldRef = useRef(''); // active bookmark field ID
  const verifyDirtyRef = useRef(false); // synced by useVerify; read by useFormNavigation

  // Callbacks into the class component — registered by FormViewer on mount.
  // Using a single object ref keeps the API surface clean.
  const navCallbacksRef = useRef({});

  // Field-events hook callbacks — populated by FormViewer in componentDidMount.
  const fieldEventsCallbacksRef = useRef({});

  // Verify hook callbacks — populated by FormViewer in componentDidMount.
  const verifyCallbacksRef = useRef({});

  // Print hook callbacks — populated by FormViewer in componentDidMount.
  const printCallbacksRef = useRef({});

  // FormPopulation hook callbacks — populated by FormViewer in componentDidMount.
  const formPopCallbacksRef = useRef({});

  // ReturnClose hook callbacks — populated by FormViewer in componentDidMount.
  const returnCloseCallbacksRef = useRef({});

  // SocketMessage comprehensive env — populated by FormViewer in componentDidMount.
  const socketCallbacksRef = useRef({});

  // ─── useCalcSession ───────────────────────────────────────────────────────

  const { calcAPIRef, socketWorkerRef, connectionStatus } = useCalcSession({
    returnID: props.returnID,
    ddh: props.ddh,
    returnInitializedRef,
    onOpen: () => onOpenRef.current?.(),
    onMessage: (cmd, api) => onMessageRef.current?.(cmd, api),
    onClose: () => onCloseRef.current?.(),
  });

  // ─── hasFLST — computed here so the navigation hook doesn't need Redux ───

  const hasFLST = useCallback(() => {
    const filingStatus = props.clientFilingStatus || props.wizardHasFLST;
    if ((!filingStatus || filingStatus === '0') && !payload?.is_business) return false;
    return true;
  }, [props.clientFilingStatus, props.wizardHasFLST, payload]);

  // ─── useFormNavigation ────────────────────────────────────────────────────

  const formNav = useFormNavigation({
    calcAPIRef,
    initialForm: props.initialForm,
    initialFile: props.initialFile,
    isWizard: props.isWizard,
    isBusiness: payload?.is_business ?? false,
    season: payload?.season ?? 0,
    isNew: props.isNew,
    startInterview: props.startInterview,
    estimatorBlob: props.estimatorBlob,
    wizardCompleted: props.wizardCompleted,
    currentElRef,
    bookmarkFieldRef,
    verifyDirtyRef,
    hasFLST,

    // Simple Redux-action callbacks resolved directly in the wrapper
    onToggleEventLog: props.toggleEventLogModal,
    onToggleTaxpass: props.toggleTaxpassMessageModal,
    onToggleK1Manager: props.toggleK1ManagerModal,

    // Callbacks that need class methods — registered by FormViewer on mount
    onFormRendered: (...a) => navCallbacksRef.current.onFormRendered?.(...a),
    onToggleGroup: (...a) => navCallbacksRef.current.onToggleGroup?.(...a),
    onInterviewStart: () => navCallbacksRef.current.onInterviewStart?.(),
    onRequireFilingStatus: () => navCallbacksRef.current.onRequireFilingStatus?.(),
    onVerifyReturn: tab => navCallbacksRef.current.onVerifyReturn?.(tab),
    onLoadVerifyForm: (...a) => navCallbacksRef.current.onLoadVerifyForm?.(...a),
    onResetVerify: (...a) => navCallbacksRef.current.onResetVerify?.(...a),
    onSendRemotePayment: (...a) => navCallbacksRef.current.onSendRemotePayment?.(...a),
    onStartSpin: msg => navCallbacksRef.current.onStartSpin?.(msg),
    onStopSpin: () => spinnerRef.current?.stopSpin(),
    // printRef is declared below but initialized before any callback can fire (after render commits)
    onSignatureFormLoad: name => printRef.current?.handleRemoteSignSelected(name),
    onCloseChoiceList: () => {
      props.toggleChoiceList(false);
      const el = document.getElementById('buttonMenuForReturns');
      if (el) el.style.display = 'none';
    },
  });

  // ─── formNavRef — kept current synchronously so jQuery handlers never go stale ──
  const formNavRef = useRef(formNav);
  formNavRef.current = formNav;

  // ─── useFormFieldEvents ───────────────────────────────────────────────────

  const fieldEvents = useFormFieldEvents({
    calcAPIRef,
    currentElRef,
    formNavRef,
    enableDoubleEntry: props.enableDoubleEntry,
    // Callbacks that delegate to class methods (registered in componentDidMount)
    onPerformCalcProcess: (...a) => fieldEventsCallbacksRef.current.onPerformCalcProcess?.(...a),
    onOpenChoiceList: (...a) => fieldEventsCallbacksRef.current.onOpenChoiceList?.(...a),
    onCheckChoiceListForValue: el =>
      fieldEventsCallbacksRef.current.onCheckChoiceListForValue?.(el),
    onHandleChoiceListItem: (...a) =>
      fieldEventsCallbacksRef.current.onHandleChoiceListItem?.(...a),
    onIsPackageAvailable: val => fieldEventsCallbacksRef.current.onIsPackageAvailable?.(val),
    onShowErrorSnackbar: msg => fieldEventsCallbacksRef.current.onShowErrorSnackbar?.(msg),
    onShowPromptSnackbar: msg => fieldEventsCallbacksRef.current.onShowPromptSnackbar?.(msg),
    onHidePromptSnackbar: () => fieldEventsCallbacksRef.current.onHidePromptSnackbar?.(),
    onSetBookmarkFldID: id => fieldEventsCallbacksRef.current.onSetBookmarkFldID?.(id),
    onSetFieldFormLinks: links => fieldEventsCallbacksRef.current.onSetFieldFormLinks?.(links),
    // Redux scroll-position actions wired directly from props
    onSetInitScrollPositionTrue: props.setInitScrollPositionTrue,
    onSetInitScrollPositionFalse: props.setInitScrollPositionFalse,
  });

  // ─── fieldEventsRef — kept current synchronously so useVerify can call fieldEvents methods ──
  const fieldEventsRef = useRef(fieldEvents);
  fieldEventsRef.current = fieldEvents;

  // ─── useVerify ────────────────────────────────────────────────────────────

  const verify = useVerify({
    calcAPIRef,
    verifyDirtyRef,
    formNavRef,
    // Callbacks that delegate to class methods (registered in componentDidMount)
    onStartSpin: msg => verifyCallbacksRef.current.onStartSpin?.(msg),
    onShowErrorSnackbar: msg => verifyCallbacksRef.current.onShowErrorSnackbar?.(msg),
    onCloseErrorRejectModal: () => verifyCallbacksRef.current.onCloseErrorRejectModal?.(),
    onViewAsset: id => verifyCallbacksRef.current.onViewAsset?.(id),
    onPerformCalcProcess: (...a) => verifyCallbacksRef.current.onPerformCalcProcess?.(...a),
    onUnbindFormEvents: () => fieldEventsRef.current.unbindFormEvents?.(),
    onBindFormEvents: () => fieldEventsRef.current.bindFormEvents?.(),
  });

  // ─── usePrint ─────────────────────────────────────────────────────────────

  const print = usePrint({
    calcAPIRef,
    formNavRef,
    printCallbacksRef,
    // Redux props
    returnID: props.returnID,
    isReadOnly: props.isReadOnly,
    usingDemoAccount: props.usingDemoAccount,
    isRMSSigningMethod: props.isRMSSigningMethod,
    preferredSignaturePad: props.preferredSignaturePad,
    isSignatureRequestActive: props.isSignatureRequestActive,
    initialSigneeData: props.initialSigneeData,
    formList: props.formList,
    eroData: props.eroData,
    preparerData: props.preparerData,
    amendedPreparerData: props.amendedPreparerData,
    encryptedPdfSelected: props.encryptedPdfSelected,
    // Redux actions
    signaturesNeededSet: props.signaturesNeededSet,
    bankAppAttachedSet: props.bankAppAttachedSet,
    setEncryptedDocumentContent: props.setEncryptedDocumentContent,
    setRMSflag: props.setRMSflag,
    inOfficeSignSuccessfulSet: props.inOfficeSignSuccessfulSet,
    setEmailBlob: props.setEmailBlob,
    signatureRequestActiveSet: props.signatureRequestActiveSet,
    showSnackbarMessage: props.showSnackbarMessage,
    showErrorDialog: props.showErrorDialog,
    setPreparerID: props.setPreparerID,
    setAmendedPreparerID: props.setAmendedPreparerID,
    setSuggestedPassword: props.setSuggestedPassword,
    togglePrintDialog: props.togglePrintDialog,
    toggleSignatureCaptureModal: props.toggleSignatureCaptureModal,
    toggleSuggestedPassword: props.toggleSuggestedPassword,
    textLinkModalContentOriginal: props.textLinkModalContentOriginal,
    toggleTextLinkSendModal: props.toggleTextLinkSendModal,
    setEncryptedPdfRequest: props.setEncryptedPdfRequest,
    toggleEncryptedPdf: props.toggleEncryptedPdf,
    setPreferredSignaturePad: props.setPreferredSignaturePad,
    payload,
  });

  // ─── useBookmarks ─────────────────────────────────────────────────────────

  const bookmarks = useBookmarks({
    calcAPIRef,
    bookmarkFieldRef,
    formNavRef,
  });

  // ─── printRef — kept current so useFormPopulation's onUpdatePrintCltr never goes stale ──
  const printRef = useRef(print);
  printRef.current = print;

  // ─── useFormPopulation ────────────────────────────────────────────────────

  const formPopulation = useFormPopulation({
    isBusiness: payload?.is_business ?? false,
    fieldEventsRef,
    onUpdatePrintCltr: value => printRef.current?.updatePrintCltr?.(value),
    onShowErrorSnackbar: msg => formPopCallbacksRef.current.onShowErrorSnackbar?.(msg),
  });

  // ─── useSpinner ───────────────────────────────────────────────────────────

  const spinner = useSpinner();

  // ─── useSnackbars ─────────────────────────────────────────────────────────

  const snackbars = useSnackbars({
    showSnackbarMessage: props.showSnackbarMessage,
    fieldEventsRef,
  });

  // ─── useReturnClose ───────────────────────────────────────────────────────

  const returnClose = useReturnClose({
    calcAPIRef,
    returnCloseCallbacksRef,
  });

  // ─── useSocketMessage ─────────────────────────────────────────────────────

  useSocketMessage({
    calcAPIRef,
    onMessageRef,
    socketCallbacksRef,
  });

  // ─── useButtonMenu ────────────────────────────────────────────────────────

  const buttonMenu = useButtonMenu();

  // ─── useCalcError ─────────────────────────────────────────────────────────

  const calcError = useCalcError();

  // ─── useK1Manager ─────────────────────────────────────────────────────────

  const k1Manager = useK1Manager({
    calcAPIRef,
    onToggleK1Manager: props.toggleK1ManagerModal,
  });

  // ─── spinnerRef — kept current so hooks that call startSpin never go stale ──
  const spinnerRef = useRef(spinner);
  spinnerRef.current = spinner;

  // ─── usePaymentCapture ────────────────────────────────────────────────────

  const paymentCapture = usePaymentCapture({ calcAPIRef, spinnerRef });

  // ─── returnIDRef — stable ref to props.returnID for hooks that need it ────
  const returnIDRef = useRef(props.returnID);
  returnIDRef.current = props.returnID;

  // ─── useDocumentAttach ────────────────────────────────────────────────────

  const documentAttach = useDocumentAttach({
    calcAPIRef,
    returnIDRef,
    onSetUpdatedAttachmentList: props.setUpdatedAttachmentList,
    onShowErrorDialog: props.showErrorDialog,
  });

  // ─── verifyRef — kept current so useQueueReturn can call verify.verifyReturn ──
  const verifyRef = useRef(verify);
  verifyRef.current = verify;

  // ─── useDepreciation ──────────────────────────────────────────────────────

  const depreciation = useDepreciation({
    calcAPIRef,
    formNavRef,
    onToggleAddFormList: props.toggleAddFormList,
    onToggleAddAssetModal: props.toggleAddAssetModal,
  });

  // ─── snackbarsRef — kept current so hooks can call snackbar methods ──────
  const snackbarsRef = useRef(snackbars);
  snackbarsRef.current = snackbars;

  // ─── useTVA ───────────────────────────────────────────────────────────────

  const tva = useTVA({
    calcAPIRef,
    formNavRef,
    onShowErrorSnackbar: msg => snackbarsRef.current?.showErrorSnackbar(msg),
  });

  // ─── useCopyReturn ────────────────────────────────────────────────────────

  const copyReturn = useCopyReturn({
    calcAPIRef,
    onShowPromptSnackbar: msg => snackbarsRef.current?.showPromptSnackbar(msg),
  });

  // ─── returnCloseRef — kept current for useSwitchReturn ────────────────────
  const returnCloseRef = useRef(returnClose);
  returnCloseRef.current = returnClose;

  // ─── notificationIDRef / notificationReturnIDRef ─────────────────────────
  const notificationIDRef = useRef(props.notificationID);
  notificationIDRef.current = props.notificationID;
  const notificationReturnIDRef = useRef(props.notificationReturnID);
  notificationReturnIDRef.current = props.notificationReturnID;

  // ─── useTextLink ──────────────────────────────────────────────────────────

  const textLink = useTextLink({
    calcAPIRef,
    returnIDRef,
    onToggleTextLinkSendModal: props.toggleTextLinkSendModal,
  });

  // ─── textLinkRef / documentAttachRef — kept current for useEncryptedPDF ───
  const textLinkRef = useRef(textLink);
  textLinkRef.current = textLink;
  const documentAttachRef = useRef(documentAttach);
  documentAttachRef.current = documentAttach;

  // ─── useSwitchReturn ──────────────────────────────────────────────────────

  const switchReturnHook = useSwitchReturn({
    returnCloseRef,
    onSelectSidebarTab: props.onSelectSidebarTab,
    onSetTitleTabProperties: props.setTitleTabProperties,
    onRequestAckNotification: props.requestAckNotification,
    onFetchReturnProfileByReturnID: props.fetchReturnProfileByReturnID,
    notificationIDRef,
    notificationReturnIDRef,
  });

  // ─── formListRef — kept current for useInterviewMode ─────────────────────
  const formListRef = useRef(props.formList);
  formListRef.current = props.formList;

  // ─── useInterviewMode ─────────────────────────────────────────────────────

  const interviewMode = useInterviewMode({
    calcAPIRef,
    formListRef,
    onInterviewMode: () => navCallbacksRef.current.onInterviewStart?.(),
  });

  // ─── useImportStock ───────────────────────────────────────────────────────

  const importStock = useImportStock();

  // ─── isReadOnlyRef / addFormFirstRef — stable refs for useAddForm ─────────
  const isReadOnlyRef = useRef(props.isReadOnly);
  isReadOnlyRef.current = props.isReadOnly;
  const addFormFirstRef = useRef(props.addFormFirst);
  addFormFirstRef.current = props.addFormFirst;

  // ─── useAddForm ───────────────────────────────────────────────────────────

  const addFormHook = useAddForm({
    calcAPIRef,
    isReadOnlyRef,
    addFormFirstRef,
    onSetAddFormPackage: props.setAddFormPackage,
    onSetFirstAddForm: props.setFirstAddForm,
    onToggleAddFormList: props.toggleAddFormList,
    onSetAddFormList: props.setAddFormList,
  });

  // ─── useQueueReturn ───────────────────────────────────────────────────────

  const queueReturn = useQueueReturn({
    calcAPIRef,
    socketWorkerRef,
    returnIDRef,
    verifyRef,
    onSetVerifyType: type => verifyRef.current?.setVerifyType(type),
    initialReadyForReview: props.readyForReview,
  });

  // ─── queueReturnRef — kept current for useSubmitForReview ────────────────
  const queueReturnRef = useRef(queueReturn);
  queueReturnRef.current = queueReturn;

  // ─── useMiscState ─────────────────────────────────────────────────────────

  const miscState = useMiscState();

  // ─── useTransferData ──────────────────────────────────────────────────────

  const transferData = useTransferData({
    calcAPIRef,
    formNavRef,
    miscState,
    spinner,
    openYTYDialog: props.openYTYDialog,
    closeYTYDialog: props.closeYTYDialog,
    openCurrentYearModal: props.openCurrentYearModal,
    closeCurrentYearModal: props.closeCurrentYearModal,
  });

  // ─── useSpecialFormLoad ───────────────────────────────────────────────────

  const specialFormLoad = useSpecialFormLoad({
    calcAPIRef,
    formNavRef,
    bookmarks,
    print,
    addForm: addFormHook,
    toggleK1ManagerModal: props.toggleK1ManagerModal,
    onStopSpin: () => spinnerRef.current.stopSpin(),
  });

  // ─── useReturnLock ────────────────────────────────────────────────────────

  const isLocked = useCallback(() => {
    return !!(
      props.lockedBy ||
      props.lockedViaAccessLevel ||
      props.lockedStatus ||
      isSuperOrTechUserReadOnly(payload) ||
      props.isReadOnly ||
      (props.officeProfile?.is_feeder_office && queueReturn.readyForReview)
    );
  }, [
    props.lockedBy,
    props.lockedViaAccessLevel,
    props.lockedStatus,
    props.isReadOnly,
    props.officeProfile,
    queueReturn.readyForReview,
    payload,
  ]);

  const returnLock = useReturnLock({
    calcAPIRef,
    isLocked,
    lockedStatus: props.lockedStatus,
    spinner,
    toggleReturnLock: props.toggleReturnLock,
    needingVerifiedSet: props.needingVerifiedSet,
  });

  // ─── useFormErrors ────────────────────────────────────────────────────────

  const formErrors = useFormErrors({
    fieldEventsRef,
    snackbarsRef,
  });

  // ─── importStockRef — kept current for useMenuCommand ────────────────────
  const importStockRef = useRef(importStock);
  importStockRef.current = importStock;

  // ─── useMenuCommand ───────────────────────────────────────────────────────

  const menuCommand = useMenuCommand({
    calcAPIRef,
    isReadOnlyRef,
    printRef,
    importStockRef,
  });

  // ─── useChoiceList ────────────────────────────────────────────────────────

  // Kept-current ref for rapidly-changing Redux choice-list state
  const choiceListDataRef = useRef({ choiceListData: [], choiceListFields: [] });
  choiceListDataRef.current = {
    choiceListData: props.choiceListData ?? [],
    choiceListFields: props.choiceListFields ?? [],
  };

  const choiceList = useChoiceList({
    calcAPIRef,
    formNavRef,
    choiceListDataRef,
    resetChoiceList: props.resetChoiceList,
    toggleChoiceList: props.toggleChoiceList,
  });

  // ─── useEncryptedPDF ──────────────────────────────────────────────────────

  // Kept-current snapshot of rapidly-changing Redux state read at call-time
  const encryptedPdfCallbacksRef = useRef({});
  encryptedPdfCallbacksRef.current = {
    encryptedPassword: props.encryptedPassword,
    encryptedPdfTitle: props.encryptedPdfTitle,
    emailBlob: props.emailBlob,
    attachmentContent: props.attachmentContent,
    taxpayerTextChecked: props.taxpayerTextChecked,
    spouseTextChecked: props.spouseTextChecked,
    taxpayerCellPhoneNumber: props.taxpayerCellPhoneNumber,
    taxpayerCellPhoneCarrier: props.taxpayerCellPhoneCarrier,
    taxpayerCellPhoneDomain: props.taxpayerCellPhoneDomain,
    spouseCellPhoneNumber: props.spouseCellPhoneNumber,
    spouseCellPhoneCarrier: props.spouseCellPhoneCarrier,
    spouseCellPhoneDomain: props.spouseCellPhoneDomain,
    taxpayerChecked: props.taxpayerChecked,
    taxpayerEPassChecked: props.taxpayerEPassChecked,
    taxpayerEmailForPdf: props.taxpayerEmailForPdf,
    spouseChecked: props.spouseChecked,
    spouseEPassChecked: props.spouseEPassChecked,
    spouseEmailForPdf: props.spouseEmailForPdf,
    representativeChecked: props.representativeChecked,
    representativeEPassChecked: props.representativeEPassChecked,
    preparerEmailForPdf: props.preparerEmailForPdf,
    isReadOnly: props.isReadOnly,
    archiveDocId: props.archiveDocId,
    isBusiness: props.isBusiness,
    returnID: props.returnID,
  };

  const encryptedPDF = useEncryptedPDF({
    calcAPIRef,
    encryptedPdfCallbacksRef,
    printRef,
    returnCloseRef,
    textLinkRef,
    documentAttachRef,
    spinnerRef,
    // Redux action dispatchers
    toggleSaveSuggestedPassword: props.toggleSaveSuggestedPassword,
    setEmailBlob: props.setEmailBlob,
    setEncryptedDocumentContent: props.setEncryptedDocumentContent,
    setEncryptedPdfRequest: props.setEncryptedPdfRequest,
    toggleSuggestedPassword: props.toggleSuggestedPassword,
    setEncryptedPdfTitle: props.setEncryptedPdfTitle,
    setSuggestedPassword: props.setSuggestedPassword,
    toggleEncryptedPdf: props.toggleEncryptedPdf,
    setEncryptedPDFSuccessDialog: props.setEncryptedPDFSuccessDialog,
    setSentSuccessTitle: props.setSentSuccessTitle,
    setSentSuccessMsg: props.setSentSuccessMsg,
    setTaxpayerSelected: props.setTaxpayerSelected,
    setTaxpayerSelectedEPass: props.setTaxpayerSelectedEPass,
    setTaxpayerSelectedText: props.setTaxpayerSelectedText,
    setSpouseSelected: props.setSpouseSelected,
    setSpouseSelectedEPass: props.setSpouseSelectedEPass,
    setSpouseSelectedText: props.setSpouseSelectedText,
    representativeSelected: props.representativeSelected,
    representativeSelectedEPass: props.representativeSelectedEPass,
    representativeSelectedText: props.representativeSelectedText,
    setArchiveDocId: props.setArchiveDocId,
  });

  // ─── useCalcProcess ───────────────────────────────────────────────────────

  const calcProcess = useCalcProcess({
    calcAPIRef,
    verifyRef,
  });

  // ─── useConsentModal ──────────────────────────────────────────────────────

  const consentModal = useConsentModal({
    calcAPIRef,
    formNavRef,
    currentElRef,
    fieldEventsRef,
  });

  // ─── attachmentsRef — kept current for usePDFAttach ───────────────────────
  const attachmentsRef = useRef(props.attachments);
  attachmentsRef.current = props.attachments;

  // ─── usePDFAttach ─────────────────────────────────────────────────────────

  const pdfAttach = usePDFAttach({
    calcAPIRef,
    returnIDRef,
    formNavRef,
    snackbarsRef,
    attachmentsRef,
  });

  // ─── miscStateRef — kept current for useRestrictedFields ─────────────────
  const miscStateRef = useRef(miscState);
  miscStateRef.current = miscState;

  // ─── useRestrictedFields ──────────────────────────────────────────────────

  const restrictedFields = useRestrictedFields({
    currentElRef,
    fieldEventsRef,
    miscStateRef,
  });

  // ─── useSubmitForReview ───────────────────────────────────────────────────

  const submitForReview = useSubmitForReview({
    calcAPIRef,
    queueReturnRef,
    returnCloseRef,
    spinnerRef,
    isReadOnlyRef,
    toggleSubmitForReviewDlg: props.toggleSubmitForReviewDlg,
  });

  // ─── useWallet ────────────────────────────────────────────────────────────

  // payloadRef — stable (JWT decoded once per session), but wrapped for hook consistency
  const payloadRef = useRef(payload);

  // officeProfileRef / ddhRef / showErrorDialogRef — kept current each render
  const officeProfileRef = useRef(props.officeProfile);
  officeProfileRef.current = props.officeProfile;
  const ddhRef = useRef(props.ddh);
  ddhRef.current = props.ddh;
  const showErrorDialogRef = useRef(props.showErrorDialog);
  showErrorDialogRef.current = props.showErrorDialog;

  const wallet = useWallet({
    calcAPIRef,
    payloadRef,
    returnIDRef,
    officeProfileRef,
    ddhRef,
    snackbarsRef,
    showErrorDialogRef,
  });

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <FormViewer
      {...props}
      // Session
      calcAPIRef={calcAPIRef}
      socketWorkerRef={socketWorkerRef}
      returnInitializedRef={returnInitializedRef}
      connectionStatus={connectionStatus}
      // Socket callback registration
      onMessageRef={onMessageRef}
      onOpenRef={onOpenRef}
      onCloseRef={onCloseRef}
      // Navigation
      formNav={formNav}
      // Navigation callback registration
      navCallbacksRef={navCallbacksRef}
      // Shared refs
      currentElRef={currentElRef}
      bookmarkFieldRef={bookmarkFieldRef}
      verifyDirtyRef={verifyDirtyRef}
      // Field events
      fieldEvents={fieldEvents}
      fieldEventsCallbacksRef={fieldEventsCallbacksRef}
      // Verify
      verify={verify}
      verifyCallbacksRef={verifyCallbacksRef}
      // Print
      print={print}
      printCallbacksRef={printCallbacksRef}
      // Bookmarks / notes
      bookmarks={bookmarks}
      // Form population (UpdateForm, PopulateForm, colorMap, group handling)
      formPopulation={formPopulation}
      formPopCallbacksRef={formPopCallbacksRef}
      // Spinner (isLoading, loadingText, startSpin, stopSpin)
      spinner={spinner}
      // Snackbars (showErrorSnackbar, showPromptSnackbar, hidePromptSnackbar, state)
      snackbars={snackbars}
      // Return close / save / delete actions + confirm dialogs
      returnClose={returnClose}
      returnCloseCallbacksRef={returnCloseCallbacksRef}
      // Socket message comprehensive env
      socketCallbacksRef={socketCallbacksRef}
      // Button menu (menuButtonOpen, openButtonMenu, closeButtonMenu)
      buttonMenu={buttonMenu}
      // Calc-engine error popup (calcErrorPopup, calcErrorTitle, calcErrorMsg)
      calcError={calcError}
      // K1 manager (k1List, k1pkgList, hideK1Flag, handleChangeHideK1s, handleToggleK1ManagerModal)
      k1Manager={k1Manager}
      // Payment capture (returnPaymentOpen, paymentTerminalStatus, returnPaymentInfo, toggleReturnPayment)
      paymentCapture={paymentCapture}
      // Document attach / preview modals
      documentAttach={documentAttach}
      // Queue return (transmit list, fees, wallet, readyForReview)
      queueReturn={queueReturn}
      // Depreciation / asset management
      depreciation={depreciation}
      // Agent Assist
      tva={tva}
      // Copy return to training / live
      copyReturn={copyReturn}
      // Text-link / SMS
      textLink={textLink}
      // Switch-return confirm dialog
      switchReturn={switchReturnHook}
      // Interview mode (addInterviewModalFlag, addInterviewFlag, confirm/cancel/show/hide)
      interviewMode={interviewMode}
      // Import stock transactions modal
      importStock={importStock}
      // Add form modal (activeAddFormTab + populateFormsAndActivitiesList/updateAddFormList)
      addForm={addFormHook}
      // Misc state (returnSaved, bankID, hasConfirmedClose, toggleTaxpassReload, etc.)
      miscState={miscState}
      // Return lock (lockFieldsIfRequired, toggleReturnStatusLock, toggleFieldLock)
      returnLock={returnLock}
      isLockedFn={isLocked}
      // Special form load response handler
      specialFormLoad={specialFormLoad}
      // Year-to-year / transfer data actions
      transferData={transferData}
      // Encrypted PDF / suggested-password flow
      encryptedPDF={encryptedPDF}
      // Choice list (openChoiceList, checkChoiceListForValue, handleChoiceListItem)
      choiceList={choiceList}
      // Menu command handler
      menuCommand={menuCommand}
      // Form error recovery (restorePreviousValue, handleFilingStatusError, onUpdateFormError)
      formErrors={formErrors}
      // Wallet (SendRemotePayment, gotoWallet, getReturnGUID, getWalletToken)
      wallet={wallet}
      // Calc process (performCalcProcess)
      calcProcess={calcProcess}
      // PDF attach (addOrAttachDocument, getDocumentIDByVar)
      pdfAttach={pdfAttach}
      // Consent modal (triggerUpdateForm, hideConsentModal)
      consentModal={consentModal}
      // Restricted fields (updateRestrictedFields, removeChoiceMenuButton)
      restrictedFields={restrictedFields}
      // Submit for review (reloadBilling, setReturnReviewStatusDialog, handleConfirmSubmitForReview)
      submitForReview={submitForReview}
    />
  );
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(FormViewerWrapper));
